import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Auth.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [status, setStatus] = useState('verifying'); // verifying | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;
    if (!token) {
      setStatus('error');
      setErrorMsg('Missing verification token.');
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Verification failed.');
        setStatus('success');
        // If the user is signed in (in this tab), refresh their /me so email_verified flips
        if (user) refreshUser();
      } catch (err) {
        setStatus('error');
        setErrorMsg(err.message);
      }
    })();
  }, [token, user, refreshUser]);

  const handleEnter = () => {
    // If already signed in, go straight to the app; otherwise send to login
    navigate(user ? '/app' : '/login', { replace: true });
  };

  return (
    <div className="auth-page">
      <div className="auth-background">
        <video className="auth-video" autoPlay loop muted playsInline>
          <source src="/hero_video.mp4" type="video/mp4" />
        </video>
        <div className="auth-video-overlay"></div>
      </div>

      <div className="auth-container">
        <div className="auth-card" style={{ maxWidth: 480, textAlign: 'center' }}>
          <div className="auth-header">
            <Link to="/" className="auth-logo" style={{ justifyContent: 'center' }}>
              <img
                src="https://customer-assets.emergentagent.com/job_5fec953c-1553-4442-b50d-48094837ff2f/artifacts/yv8qmlbd_AI_Architecture_Logo__2_-removebg-preview.png"
                alt="Ezai Build"
                className="logo-icon"
                style={{ width: 44, height: 44 }}
              />
              <span className="logo-text">Ezai Build</span>
            </Link>
          </div>

          {status === 'verifying' && (
            <div data-testid="verify-loading">
              <Loader2 size={48} className="spinner" style={{ marginBottom: 16 }} />
              <h2 className="heading-2">Verifying your email…</h2>
            </div>
          )}

          {status === 'success' && (
            <div data-testid="verify-success">
              <div style={{
                margin: '0 auto 20px',
                width: 88,
                height: 88,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(0,255,209,0.25) 0%, rgba(0,255,209,0.05) 60%, transparent 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <CheckCircle2 size={64} color="var(--brand-primary)" />
              </div>
              <h2 className="display-medium auth-title" style={{ marginTop: 0 }}>You're verified!</h2>
              <p className="body-medium auth-subtitle" style={{ maxWidth: 380, margin: '12px auto 0' }}>
                Welcome to Ezai Build. Your account is ready — upload a floor plan and turn it into a stunning 3D visualization in seconds.
              </p>

              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 32 }}>
                <Button
                  className="btn-primary btn-large"
                  onClick={handleEnter}
                  data-testid="enter-app-btn"
                  style={{ minWidth: 220 }}
                >
                  <Sparkles size={18} />
                  <span style={{ marginLeft: 8 }}>Enter Ezai Build</span>
                </Button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div data-testid="verify-error">
              <XCircle size={56} color="#ff6b6b" style={{ marginBottom: 16 }} />
              <h2 className="heading-2">Verification failed</h2>
              <p className="body-medium auth-error" style={{ marginTop: 16 }}>{errorMsg}</p>
              <Link to="/login" className="auth-link" style={{ display: 'inline-block', marginTop: 20 }}>
                Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
